<template>
  <div class="container-fluid mt-4">
    <div class="card">
      <div class="card-header p-3 d-flex justify-content-between align-items-center">
        <h5 class="mt-1 mb-0">
          Test Billing Logic
        </h5>
        <BulkUploadCsvButton
          uploadAction="testBilling/uploadCsv"
          :actionPayload="{ brandId: selectedBrandId }"
          :disabled="!selectedBrandId || isRunning"
          @upload-start="resetView"
          @uploaded="handleUploaded"
        />
      </div>
      <div class="card-body p-3">
        <div class="row mb-3">
          <div class="col-md-4">
            <SelectInput
              v-model="selectedBrandId"
              :options="brandOptions"
              label="Brand"
            />
            <p class="text-muted small mb-0">
              Upload a CSV file or paste rows below.
              No data will be persisted.
            </p>
          </div>
          <div class="col-md-4">
            <label class="form-label">Sample CSV (paste rows)</label>
            <textarea
              v-model="csvText"
              class="form-control"
              rows="6"
              placeholder="location,serviceLevel,orderId,brand,shipmentDate,createdDate,qty,trackingIds,ca1,totalPrice,zip,length,width,height,scaleWeight"
            />
          </div>
          <div class="col-md-4 d-flex flex-column">
            <button
              :disabled="!selectedBrandId || !csvText || isRunning"
              class="btn btn-primary mt-3"
              @click="runHandler"
            >
              {{ isRunning ? "Running…" : "Run Trial" }}
            </button>
            <button
              :disabled="isRunning"
              class="btn btn-outline-secondary mt-2"
              @click="resetHandler"
            >
              Reset
            </button>
          </div>
        </div>
        <CsvFormatRequirements
          :columns="csvColumns"
          :description="csvDescription"
        />
        <div
          v-if="result && result.warning"
          class="alert alert-warning"
        >
          {{ result.warning }}
        </div>
        <div
          v-if="result"
          class="alert alert-info"
        >
          Processed {{ result.processedRows }} / {{ result.totalRows }} rows. Click a row to see its decision paths.
        </div>
        <div
          v-if="rows.length"
          class="row align-items-end"
        >
          <div class="col-md-4">
            <SearchForm
              v-model="tableSearchInput"
              placeHolder="Search by order or tracking ID..."
              @search="submitTableSearch"
              @clear="clearTableSearch"
            />
          </div>
          <div class="col-md-8 d-flex justify-content-end align-items-center gap-3 mb-3">
            <span class="text-muted small">
              Showing {{ filteredRows.length }} of {{ rows.length }} results
            </span>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              :disabled="!filteredRows.length"
              @click="downloadResults"
            >
              <i class="bi bi-download" />
              Download Full Report CSV
            </button>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              :disabled="!filteredRows.length"
              @click="downloadSimplifiedResults"
            >
              <i class="bi bi-download" />
              Download Simplified CSV
            </button>
          </div>
        </div>
        <div
          v-if="rows.length && !filteredRows.length"
          class="alert alert-info"
        >
          No results for "{{ tableSearch }}"
        </div>
        <div
          v-if="filteredRows.length"
          style="height: 60vh"
        >
          <ag-grid-vue
            :columnDefs="columnDefs"
            :defaultColDef="defaultColDef"
            :pagination="true"
            :paginationAutoPageSize="true"
            :rowData="gridRows"
            :isFullWidthRow="isFullWidthRow"
            fullWidthCellRenderer="TestBillingDetailRenderer"
            :getRowHeight="getRowHeight"
            :getRowId="getRowId"
            class="ag-theme-alpine h-100 w-100"
            @cellClicked="onCellClicked"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { AgGridVue } from 'ag-grid-vue3';
import SelectInput from '@/components/common/SelectInput';
import BulkUploadCsvButton from '@/components/common/BulkUploadCsvButton.vue';
import TestBillingDetailRenderer from '@/components/TestBilling/TestBillingDetailRenderer.vue';
import CsvFormatRequirements from '@/components/common/CsvFormatRequirements.vue';
import SearchForm from '@/components/common/SearchForm.vue';
import { formatRateBase } from '@/components/DecisionTrees/rateBaseLabels';
import {
  testBillingCsvColumns,
  testBillingCsvDescription,
  testBillingResultCsvColumns,
  testBillingSimplifiedCsvColumns,
} from '@/components/TestBilling/constants';
import tableControls from '@/mixins/tableControls';
import { datedCsvFilename, downloadCsv } from '@/utils/download';
import { decimalFormatter, WEIGHT_DECIMALS } from '@/utils/number';

const REQUIRED_COLUMNS = [
  'location',
  'serviceLevel',
  'orderId',
  'brand',
  'shipmentDate',
  'createdDate',
  'qty',
  'trackingIds',
  'ca1',
  'totalPrice',
  'zip',
  'length',
  'width',
  'height',
  'scaleWeight',
];

const DETAIL_ROW_BASE_HEIGHT = 70;
const DETAIL_ROW_LINE_HEIGHT = 24;

const parseCsv = (text) => {
  const lines = text
    .trim()
    .split('\n')
    .filter((l) => l.trim().length);
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map((h) => h.trim());
  return lines.slice(1).map((line) => {
    const cells = line.split(',').map((c) => c.trim());
    const row = {};
    headers.forEach((h, i) => {
      row[h] = cells[i] != null && cells[i] !== '' ? cells[i] : null;
    });
    [ 'qty', 'length', 'width', 'height', 'scaleWeight', 'totalPrice' ].forEach((f) => {
      if (row[f] != null) row[f] = Number(row[f]);
    });
    return row;
  });
};

export default {
  components: {
    AgGridVue,
    SelectInput,
    BulkUploadCsvButton,
    CsvFormatRequirements,
    SearchForm,
    // eslint-disable-next-line vue/no-unused-components
    TestBillingDetailRenderer,
  },
  mixins: [tableControls()],
  data() {
    return {
      selectedBrandId: null,
      csvText: '',
      isRunning: false,
      expandedRows: {},
      csvColumns: testBillingCsvColumns,
      csvDescription: testBillingCsvDescription,
    };
  },
  computed: {
    ...mapGetters({
      result: 'testBilling/result',
      brandNames: 'brandManagement/allBrandNames',
    }),
    brandOptions() {
      return Object.entries(this.brandNames || {})
        .map(([ id, brand ]) => ({ key: id, value: brand.name }))
        .sort((a, b) => String(a.value).localeCompare(String(b.value)));
    },
    selectedBrandName() {
      return this.brandNames?.[this.selectedBrandId]?.name || this.selectedBrandId;
    },
    rows() {
      return this.result?.results || [];
    },
    filteredRows() {
      return this.rows.filter(this.matchesSearch);
    },
    // Result rows interleaved with a synthetic full-width row for each expanded result.
    gridRows() {
      const out = [];
      this.rows.forEach((row, index) => {
        if (!this.matchesSearch(row)) {
          return;
        }
        out.push({ ...row, __index: index });
        if (this.expandedRows[index]) {
          out.push({
            __index: index,
            __detail: true,
            decisionPath: row.decisionPath,
            ahsDecisionPath: row.ahsDecisionPath,
          });
        }
      });
      return out;
    },
    defaultColDef() {
      return { resizable: true, sortable: true, filter: true, wrapHeaderText: true };
    },
    columnDefs() {
      return [
        {
          headerName: '',
          field: '__index',
          width: 50,
          sortable: false,
          filter: false,
          valueFormatter: (params) => (this.expandedRows[params.value] ? '▾' : '▸'),
        },
        { headerName: 'Order', field: 'orderId', width: 130 },
        { headerName: 'Brand', field: 'brand', width: 90 },
        { headerName: 'Shipvia', field: 'shipvia', width: 130 },
        { headerName: 'Zone', field: 'zone', width: 80 },
        {
          headerName: 'Bill weight',
          field: 'billWeight',
          width: 110,
          valueFormatter: decimalFormatter(WEIGHT_DECIMALS),
        },
        {
          headerName: 'Rate base',
          field: 'rateBase',
          width: 180,
          valueFormatter: (params) => formatRateBase(params.value),
        },
        {
          headerName: 'Base rate',
          field: 'baseShippingRate',
          width: 110,
          valueFormatter: (params) => params.data?.baseShippingRate?.toFixed(2),
        },
        {
          headerName: 'Shipping rate',
          field: 'shippingRate',
          width: 120,
          valueFormatter: (params) => params.data?.shippingRate?.toFixed(2),
        },
        {
          headerName: 'Margin',
          field: 'margin',
          width: 100,
          valueFormatter: (params) => params.data?.margin?.toFixed(2),
        },
        {
          headerName: 'Surcharges',
          field: 'surcharges',
          width: 110,
          valueFormatter: (params) => params.data?.surcharges?.toFixed(2),
        },
        { headerName: 'AHS', field: 'ahs', width: 90, valueFormatter: (params) => params.data?.ahs?.toFixed(2) },
        { headerName: 'DAS', field: 'das', width: 90, valueFormatter: (params) => params.data?.das?.toFixed(2) },
        { headerName: 'EDAS', field: 'edas', width: 90, valueFormatter: (params) => params.data?.edas?.toFixed(2) },
        { headerName: 'RDAS', field: 'rdas', width: 90, valueFormatter: (params) => params.data?.rdas?.toFixed(2) },
        {
          headerName: 'Fuel',
          field: 'fuelFee',
          width: 90,
          valueFormatter: (params) => params.data?.fuelFee?.toFixed(2),
        },
        {
          headerName: 'Delivery',
          field: 'deliveryFee',
          width: 100,
          valueFormatter: (params) => params.data?.deliveryFee?.toFixed(2),
        },
        {
          headerName: 'Total shipping',
          field: 'totalShippingPrice',
          width: 130,
          valueFormatter: (params) => params.data?.totalShippingPrice?.toFixed(2),
        },
      ];
    },
  },
  async mounted() {
    if (!this.brandNames || Object.keys(this.brandNames).length === 0) {
      await this.fetchBrandNames();
    }
  },
  methods: {
    ...mapActions({
      setInputRows: 'testBilling/setInputRows',
      runTrial: 'testBilling/runTrial',
      resetTrial: 'testBilling/reset',
      fetchBrandNames: 'brandManagement/fetchBrandNames',
    }),
    matchesSearch(row) {
      const term = this.tableSearch.toLowerCase();
      return !term || [ row.orderId, row.trackingIds ].some((value) => String(value ?? '').toLowerCase().includes(term));
    },
    downloadResults() {
      downloadCsv(
        this.filteredRows,
        testBillingResultCsvColumns,
        datedCsvFilename('test-billing-results', this.selectedBrandName)
      );
    },
    downloadSimplifiedResults() {
      const rows = this.filteredRows.map((row) => ({ ...row, rateBase: formatRateBase(row.rateBase) }));
      downloadCsv(
        rows,
        testBillingSimplifiedCsvColumns,
        datedCsvFilename('test-billing-results-simplified', this.selectedBrandName)
      );
    },
    resetView() {
      this.expandedRows = {};
      this.tableSearchInput = '';
      this.tableSearch = '';
    },
    isFullWidthRow(params) {
      return !!params.rowNode.data?.__detail;
    },
    getRowId(params) {
      return params.data.__detail ? `detail-${params.data.__index}` : `row-${params.data.__index}`;
    },
    getRowHeight(params) {
      if (!params.data?.__detail) {
        return null;
      }
      const lines = Math.max(
        (params.data.decisionPath || []).length,
        (params.data.ahsDecisionPath || []).length,
        1
      );
      return DETAIL_ROW_BASE_HEIGHT + lines * DETAIL_ROW_LINE_HEIGHT;
    },
    onCellClicked(event) {
      if (!event.data || event.data.__detail) {
        return;
      }
      const index = event.data.__index;
      this.expandedRows = { ...this.expandedRows, [index]: !this.expandedRows[index] };
    },
    async runHandler() {
      const rows = parseCsv(this.csvText);
      if (!rows.length) {
        this.$toast.error('No rows parsed from CSV.');
        return;
      }
      const missing = REQUIRED_COLUMNS.filter((c) => !(c in rows[0]));
      if (missing.length) {
        this.$toast.error('Missing columns: ' + missing.join(', '));
        return;
      }
      this.isRunning = true;
      this.resetView();
      try {
        await this.setInputRows(rows);
        await this.runTrial(this.selectedBrandId);
        this.$toast.success('Trial calculation finished.');
      } catch (e) {
        this.$toast.error('Trial calculation failed.');
      } finally {
        this.isRunning = false;
      }
    },
    handleUploaded(report) {
      if (report && Array.isArray(report.results)) {
        this.$toast.success('Trial calculation finished.');
      }
    },
    async resetHandler() {
      await this.resetTrial();
      this.csvText = '';
      this.resetView();
    },
  },
};
</script>
